import {
  ArrowLeft,
  Save,
  Trash2,
  Gift,
  Copy,
  XCircle,
  Coins,
  DollarSign,
  CalendarPlus,
  Pencil,
  UserPlus,
  Users,
  Link2,
  CheckCircle2,
  Clock,
  RotateCcw,
} from 'lucide-react';
import { useState, useEffect } from 'react';

import AddUsageCreditsModal from '@/components/AddUsageCreditsModal';
import ApplyReferralModal from '@/components/ApplyReferralModal';
import CreateTrialTeamSection from '@/components/CreateTrialTeamSection';
import CreditsModal from '@/components/CreditsModal';
import DiscountModal from '@/components/DiscountModal';
import GrantTrialModal from '@/components/GrantTrialModal';
import RemoveUsageCreditsModal from '@/components/RemoveUsageCreditsModal';
import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { UserCreditWalletSection } from '@/components/UserCreditWalletSection';
import UserEditTeamSection from '@/components/UserEditTeamSection';
import { ExtendTrialDialog } from '@/features/accounts/ExtendTrialDialog';
import { PaymentLinkDialog } from '@/features/accounts/PaymentLinkDialog';
import {
  useUpdateUser,
  useDeleteUser,
  useGenerateCoupon,
  useCancelSubscription,
  useGrantTrial,
  useResetDailyQuota,
  useResetDailyAllowance,
  useResetMonthlyAllowance,
  useUserCreditWallet,
} from '@/hooks/useUsers';
import type { UserDetail } from '@/types/users';

interface UserEditProps {
  user: UserDetail;
  onBack: () => void;
}

const PLAN_OPTIONS = [
  { value: 'basic', label: 'Basic' },
  { value: 'plus_monthly', label: 'Plus Monthly' },
  { value: 'plus_annual', label: 'Plus Annual' },
  { value: 'power_monthly', label: 'Power Monthly' },
  { value: 'power_annual', label: 'Power Annual' },
  { value: 'pro_monthly', label: 'Pro Monthly' },
  { value: 'pro2_monthly', label: 'Pro2 Monthly' },
];

function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export default function UserEdit({ user, onBack }: UserEditProps) {
  const [selectedPlan, setSelectedPlan] = useState(user.plan || 'basic');
  const [hasSignedBaa, setHasSignedBaa] = useState(
    user.feature_flags?.has_signed_baa ?? false
  );
  const [hasStudentDiscount, setHasStudentDiscount] = useState(
    user.feature_flags?.student_discount ?? false
  );
  const [studentDiscountExpiresAt, setStudentDiscountExpiresAt] = useState(
    isoToDateInput(user.feature_flags?.student_discount_expires_at ?? null)
  );
  const [savedStudentDiscount, setSavedStudentDiscount] = useState({
    on: user.feature_flags?.student_discount ?? false,
    expires: isoToDateInput(
      user.feature_flags?.student_discount_expires_at ?? null
    ),
  });
  const [studentDiscountError, setStudentDiscountError] = useState<
    string | null
  >(null);

  useEffect(() => {
    setSelectedPlan(user.plan || 'basic');
  }, [user.plan]);

  useEffect(() => {
    setHasSignedBaa(user.feature_flags?.has_signed_baa ?? false);
  }, [user.feature_flags?.has_signed_baa]);

  useEffect(() => {
    const on = user.feature_flags?.student_discount ?? false;
    const expires = isoToDateInput(
      user.feature_flags?.student_discount_expires_at ?? null
    );
    setHasStudentDiscount(on);
    setStudentDiscountExpiresAt(expires);
    setSavedStudentDiscount({ on, expires });
    setStudentDiscountError(null);
  }, [
    user.feature_flags?.student_discount,
    user.feature_flags?.student_discount_expires_at,
  ]);

  const [couponCode, setCouponCode] = useState<{
    code: string;
    planLabel: string | undefined;
  } | null>(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showAddUsageCreditsModal, setShowAddUsageCreditsModal] =
    useState(false);
  const [showRemoveUsageCreditsModal, setShowRemoveUsageCreditsModal] =
    useState(false);
  const { data: creditWallet } = useUserCreditWallet(user.id);
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const generateCoupon = useGenerateCoupon();
  const cancelSubscription = useCancelSubscription();
  const grantTrial = useGrantTrial();
  const [accountsDialog, setAccountsDialog] = useState<
    'extend' | 'payment' | null
  >(null);
  const resetDailyQuota = useResetDailyQuota();
  const resetDailyAllowance = useResetDailyAllowance();
  const resetMonthlyAllowance = useResetMonthlyAllowance();
  const toast = useToast();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePlanChange = (plan: string) => {
    setSelectedPlan(plan);
  };

  const handleSave = async () => {
    try {
      const updateData: { userId: number; plan?: string } = { userId: user.id };
      if (selectedPlan !== user.plan) updateData.plan = selectedPlan;

      await updateUser.mutateAsync(updateData);
      toast.success('Changes saved');
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(
        'Failed to update user',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  };

  const hasChanges = selectedPlan !== user.plan && !user.team;
  const isBasicPlan = (user.plan ?? 'basic') === 'basic';

  const handleGenerateCoupon = async (
    plan: string,
    discount_percentage: number,
    duration_months: number | null
  ) => {
    try {
      const response = await generateCoupon.mutateAsync({
        email: user.email,
        plan,
        discount_percentage,
        duration_months,
      });
      setCouponCode({
        code: response.coupon_code,
        planLabel: PLAN_OPTIONS.find((option) => option.value === plan)?.label,
      });
      setShowDiscountModal(false);
      toast.success('Discount code generated');
    } catch (error) {
      toast.error(
        'Failed to generate coupon',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  };

  const handleCancelSubscription = async () => {
    const confirmation = window.confirm(
      `Are you sure you want to cancel the subscription for:\n\n${user.email}\n\nThe subscription will remain active until the end of the current billing period, and the user will receive a cancellation email.`
    );

    if (!confirmation) return;

    try {
      const response = await cancelSubscription.mutateAsync(user.id);
      toast.success('Subscription cancelled', response.message);
    } catch (error) {
      toast.error(
        'Failed to cancel subscription',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  };

  const handleGrantTrial = async (plan: string, trialEnd: string) => {
    try {
      const response = await grantTrial.mutateAsync({
        userId: user.id,
        plan,
        trial_end: trialEnd,
      });
      setShowTrialModal(false);
      toast.success('Trial granted', response.message);
    } catch (error) {
      toast.error(
        'Failed to grant trial',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  };

  const handleResetDailyQuota = async () => {
    const confirmed = window.confirm(
      `Reset daily chat quota for ${user.email}?\n\nThey'll get a full fresh daily quota for the rest of today (until their local midnight).`
    );
    if (!confirmed) return;
    try {
      await resetDailyQuota.mutateAsync(user.id);
      toast.success(`Quota reset for ${user.email}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to reset quota'
      );
    }
  };

  const handleResetDailyAllowance = async () => {
    const confirmed = window.confirm(
      `Reset daily credit allowance for ${user.email}?\n\nThis expires their current daily credit balance and grants a fresh full daily allowance for today.`
    );
    if (!confirmed) return;
    try {
      const res = await resetDailyAllowance.mutateAsync(user.id);
      toast.success(
        res.reset
          ? `Daily allowance reset for ${user.email}`
          : `No daily allowance to reset for ${user.email}`
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to reset daily allowance'
      );
    }
  };

  const handleResetMonthlyAllowance = async () => {
    if (isBasicPlan) {
      toast.error(
        'No monthly allowance',
        'Basic has no monthly allowance to reset.'
      );
      return;
    }
    const confirmed = window.confirm(
      `Reset monthly allowance for ${user.email}?\n\nThis expires their current plan credit balance and grants a fresh full monthly allowance for the current cycle.`
    );
    if (!confirmed) return;
    try {
      const res = await resetMonthlyAllowance.mutateAsync(user.id);
      toast.success(
        res.reset
          ? `Monthly allowance reset for ${user.email}`
          : `No monthly allowance to reset for ${user.email}`
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to reset monthly allowance'
      );
    }
  };

  const handleDelete = async () => {
    const confirmation = window.confirm(
      `⚠️ WARNING: This action is IRREVERSIBLE!\n\nAre you sure you want to permanently delete:\n\n${user.email} (${user.name || 'No name'})\n\nThis will permanently delete:\n• User account\n• All chats and messages\n• All journals and summaries\n• All associated data`
    );

    if (!confirmation) return;

    try {
      await deleteUser.mutateAsync(user.id);
      toast.success('User deleted successfully');
      onBack();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(
        'Failed to delete user',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Edit User</CardTitle>
            <CardDescription>
              Update user information and plan status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {user.cancel_at_period_end && (
              <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
                <p className="font-semibold">
                  ⚠️ Subscription Scheduled for Cancellation
                </p>
                <p className="text-sm mt-1">
                  This user's subscription will be cancelled at the end of the
                  current billing period.
                </p>
              </div>
            )}

            {user.team && (
              <div className="bg-primary/5 border border-primary/30 text-foreground px-4 py-3 rounded-md">
                <p className="font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  This user is on a team
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Their plan and billing are managed by{' '}
                  <span className="font-medium">{user.team.name}</span>.
                  Sections below are labeled with their scope. The Team section
                  affects all{' '}
                  {
                    user.team.members.filter((m) => m.status === 'active')
                      .length
                  }{' '}
                  members.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Name
                </label>
                <p className="text-sm mt-1">{user.name || 'No name'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Email
                </label>
                <p className="text-sm mt-1">{user.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Signup Date
                </label>
                <p className="text-sm mt-1">{formatDate(user.signup_date)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  User Persona
                </label>
                <p className="text-sm mt-1">
                  {user.posthog_person?.properties?.user_persona || '-'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Predicted Profession
                </label>
                <p className="text-sm mt-1">{user.profession || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Stripe invoice credit
                </label>
                <p className="text-sm mt-1">
                  {user.account_credit_cents != null
                    ? `$${(user.account_credit_cents / 100).toFixed(2)}`
                    : '-'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Referral Code
                </label>
                {user.referral_code ? (
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm font-mono bg-muted px-2 py-0.5 rounded">
                      {user.referral_code}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() => {
                        navigator.clipboard.writeText(user.referral_code!);
                        toast.success('Referral code copied');
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm mt-1">-</p>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Intended Use
              </label>
              {user.posthog_person?.properties?.intended_use &&
              user.posthog_person.properties.intended_use.length > 0 ? (
                <ul className="list-disc list-inside space-y-1">
                  {user.posthog_person.properties.intended_use.map(
                    (use, idx) => (
                      <li key={idx} className="text-sm">
                        {use}
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <p className="text-sm">-</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Predicted Characteristic Tags
              </label>
              {user.characteristic_tags &&
              user.characteristic_tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.characteristic_tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm">-</p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={hasSignedBaa}
                disabled={updateUser.isPending}
                onCheckedChange={async (checked) => {
                  setHasSignedBaa(checked);
                  try {
                    await updateUser.mutateAsync({
                      userId: user.id,
                      feature_flags: { has_signed_baa: checked },
                    });
                    toast.success(
                      `BAA flag ${checked ? 'enabled' : 'disabled'}`
                    );
                  } catch (error) {
                    setHasSignedBaa(!checked);
                    toast.error(
                      'Failed to update BAA flag',
                      error instanceof Error ? error.message : 'Unknown error'
                    );
                  }
                }}
              />
              <div>
                <label className="text-sm font-medium">Signed BAA</label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Turn this on after a user has signed a BAA contract with us.
                  These users are treated with special data handling
                  requirements.
                </p>
              </div>
            </div>

            {(() => {
              const hasPendingChanges =
                hasStudentDiscount !== savedStudentDiscount.on ||
                studentDiscountExpiresAt !== savedStudentDiscount.expires;
              const saveStudentDiscount = async () => {
                setStudentDiscountError(null);
                let expiresAtIso: string | null = null;
                if (hasStudentDiscount) {
                  if (!studentDiscountExpiresAt) {
                    setStudentDiscountError(
                      'Set an expiration date in the future.'
                    );
                    return;
                  }
                  const d = new Date(`${studentDiscountExpiresAt}T00:00:00Z`);
                  if (isNaN(d.getTime()) || d.getTime() <= Date.now()) {
                    setStudentDiscountError(
                      'Expiration must be a future date.'
                    );
                    return;
                  }
                  expiresAtIso = d.toISOString();
                }
                try {
                  await updateUser.mutateAsync({
                    userId: user.id,
                    feature_flags: {
                      student_discount: hasStudentDiscount,
                      student_discount_expires_at: expiresAtIso,
                    },
                  });
                  setSavedStudentDiscount({
                    on: hasStudentDiscount,
                    expires: hasStudentDiscount ? studentDiscountExpiresAt : '',
                  });
                  toast.success(
                    `Student discount ${hasStudentDiscount ? 'enabled' : 'disabled'}`
                  );
                } catch (error) {
                  setStudentDiscountError(
                    error instanceof Error
                      ? error.message
                      : 'Failed to update student discount'
                  );
                }
              };
              return (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={hasStudentDiscount}
                      disabled={updateUser.isPending}
                      onCheckedChange={(checked) => {
                        setHasStudentDiscount(checked);
                        if (!checked) setStudentDiscountExpiresAt('');
                        setStudentDiscountError(null);
                      }}
                    />
                    <div>
                      <label className="text-sm font-medium">
                        Student Discount
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Turn this on for users who are eligible for the student
                        discount.
                      </p>
                    </div>
                  </div>

                  {hasStudentDiscount && (
                    <div className="pl-12 space-y-1">
                      <label className="text-xs font-medium text-muted-foreground block">
                        Discount expires at
                      </label>
                      <input
                        type="date"
                        value={studentDiscountExpiresAt}
                        disabled={updateUser.isPending}
                        onChange={(e) => {
                          setStudentDiscountExpiresAt(e.target.value);
                          setStudentDiscountError(null);
                        }}
                        className="border rounded-md px-2 py-1 text-sm bg-background"
                      />
                      <p className="text-xs text-muted-foreground">
                        When this passes, the discount is auto-revoked and any
                        active student-priced subscription is moved to the
                        default Plus monthly price at the next billing cycle.
                      </p>
                    </div>
                  )}

                  {studentDiscountError && (
                    <p className="text-xs text-destructive pl-12">
                      {studentDiscountError}
                    </p>
                  )}

                  {hasPendingChanges && (
                    <div className="pl-12">
                      <Button
                        size="sm"
                        onClick={saveStudentDiscount}
                        disabled={updateUser.isPending}
                      >
                        {updateUser.isPending
                          ? 'Saving...'
                          : 'Save student discount'}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Activity Stats
              </label>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="border rounded-md p-3">
                  <p className="text-xs text-muted-foreground">Chats</p>
                  <p className="text-xl font-semibold mt-1">
                    {user.total_chats}
                  </p>
                </div>
                <div className="border rounded-md p-3">
                  <p className="text-xs text-muted-foreground">
                    Routine Templates
                  </p>
                  <p className="text-xl font-semibold mt-1">
                    {user.total_routine_templates}
                  </p>
                  {user.routines_by_template &&
                    Object.keys(user.routines_by_template).length > 0 && (
                      <div className="mt-2 pt-2 border-t space-y-0.5">
                        {Object.entries(user.routines_by_template).map(
                          ([template, count]) =>
                            count !== undefined && (
                              <div
                                key={template}
                                className="flex justify-between gap-2 text-xs"
                              >
                                <span
                                  className="text-muted-foreground truncate"
                                  title={template}
                                >
                                  {template}
                                </span>
                                <span className="font-medium flex-shrink-0">
                                  {count}
                                </span>
                              </div>
                            )
                        )}
                      </div>
                    )}
                </div>
                <div className="border rounded-md p-3">
                  <p className="text-xs text-muted-foreground">
                    Total Meetings
                  </p>
                  <p className="text-xl font-semibold mt-1">
                    {user.total_meetings}
                  </p>
                </div>
                <div className="border rounded-md p-3">
                  <p className="text-xs text-muted-foreground">Journals</p>
                  <p className="text-xl font-semibold mt-1">
                    {user.total_journals}
                  </p>
                </div>
              </div>
            </div>

            <UserCreditWalletSection userId={user.id} />

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Credit Actions
              </label>
              <div className="border rounded-md divide-y">
                <div className="flex flex-col items-start gap-1.5 p-3">
                  <Button
                    onClick={() => setShowAddUsageCreditsModal(true)}
                    variant="outline"
                    size="sm"
                  >
                    <Coins className="h-4 w-4 mr-2" />
                    Add Usage Credits
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Add comped usage credits from a dollar value to the user's
                    purchased balance — never expires, spent last.
                  </p>
                </div>
                <div className="flex flex-col items-start gap-1.5 p-3">
                  <Button
                    onClick={() => setShowRemoveUsageCreditsModal(true)}
                    variant="outline"
                    size="sm"
                  >
                    <Coins className="h-4 w-4 mr-2" />
                    Remove Usage Credits
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Take a dollar value of credits back from the user's
                    purchased balance, such as after a Stripe refund.
                  </p>
                </div>
                <div className="flex flex-col items-start gap-1.5 p-3">
                  <Button
                    onClick={handleResetDailyAllowance}
                    variant="outline"
                    size="sm"
                    disabled={resetDailyAllowance.isPending}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {resetDailyAllowance.isPending
                      ? 'Resetting...'
                      : 'Reset Daily Allowance'}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Expire the current daily credit balance and grant a fresh
                    full daily allowance for today.
                  </p>
                </div>
                <div className="flex flex-col items-start gap-1.5 p-3">
                  <Button
                    onClick={handleResetMonthlyAllowance}
                    variant="outline"
                    size="sm"
                    disabled={resetMonthlyAllowance.isPending}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {resetMonthlyAllowance.isPending
                      ? 'Resetting...'
                      : 'Reset Monthly Allowance'}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Expire the current plan credit balance and grant a fresh
                    full monthly allowance for the cycle (paid tiers only).
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Referrals
              </label>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="border rounded-md p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">
                        Referred By
                      </p>
                    </div>
                    {user.referred_by ? (
                      <p
                        className="text-sm font-medium truncate"
                        title={user.referred_by.referrer_email}
                      >
                        {user.referred_by.referrer_email}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">None</p>
                    )}
                  </div>
                  <div className="border rounded-md p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">
                        Referrals Made
                      </p>
                    </div>
                    <p className="text-xl font-semibold">
                      {user.referrals_made?.length ?? 0}
                    </p>
                  </div>
                  <div className="border rounded-md p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">
                        Total Referral Credits Earned
                      </p>
                    </div>
                    <p className="text-xl font-semibold">
                      $
                      {(
                        (user.total_referral_credits_earned_cents ?? 0) / 100
                      ).toFixed(2)}
                    </p>
                  </div>
                </div>

                {user.referred_by && (
                  <div className="border rounded-md p-4">
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                      Referral Source
                    </p>
                    <div className="grid grid-cols-1 gap-y-2 sm:grid-cols-2 sm:gap-x-8">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Referrer
                        </span>
                        <span className="text-sm font-medium">
                          {user.referred_by.referrer_name ||
                            user.referred_by.referrer_email}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Date
                        </span>
                        <span className="text-sm font-medium">
                          {user.referred_by.date}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Credit Applied
                        </span>
                        <span className="text-sm font-medium">
                          $
                          {(
                            user.referred_by.credit_applied_cents / 100
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Status
                        </span>
                        <Badge
                          variant={
                            user.referred_by.status === 'successful'
                              ? 'default'
                              : 'secondary'
                          }
                          className={
                            user.referred_by.status === 'successful'
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                              : 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-50'
                          }
                        >
                          {user.referred_by.status === 'successful' ? (
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                          ) : (
                            <Clock className="h-3 w-3 mr-1" />
                          )}
                          {user.referred_by.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}

                {(user.referrals_made?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                      Referral History
                    </p>
                    <div className="border rounded-md overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50 hover:bg-muted/50">
                            <TableHead className="h-9 px-3 text-xs">
                              Email
                            </TableHead>
                            <TableHead className="h-9 px-3 text-xs">
                              Date
                            </TableHead>
                            <TableHead className="h-9 px-3 text-xs">
                              Status
                            </TableHead>
                            <TableHead className="h-9 px-3 text-xs text-right">
                              Credit Earned
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {user.referrals_made?.map((r, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="py-2.5 px-3 font-medium">
                                {r.referred_email}
                              </TableCell>
                              <TableCell className="py-2.5 px-3 text-muted-foreground">
                                {r.date}
                              </TableCell>
                              <TableCell className="py-2.5 px-3">
                                <Badge
                                  variant="secondary"
                                  className={
                                    r.status === 'successful'
                                      ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                      : 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-50'
                                  }
                                >
                                  {r.status === 'successful' ? (
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                  ) : (
                                    <Clock className="h-3 w-3 mr-1" />
                                  )}
                                  {r.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2.5 px-3 text-right font-medium">
                                {r.credit_earned_cents != null
                                  ? `$${(r.credit_earned_cents / 100).toFixed(2)}`
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {user.hubspot_contact_record &&
              (() => {
                const props = user.hubspot_contact_record.properties;

                const totalEvents = [
                  { label: 'Chat Events', value: props.all_chat_events },
                  { label: 'Routine Events', value: props.all_routine_events },
                  {
                    label: 'Journal Opens',
                    value: props.times_journals_opened,
                  },
                ];

                const lastActivity = [
                  { label: 'Last Active', key: props.last_active_date },
                  {
                    label: 'Last Chat Activity',
                    key: props.last_chat_activity,
                  },
                  {
                    label: 'Last Routine Activity',
                    key: props.last_routine_activity,
                  },
                  {
                    label: 'Last Journal Activity',
                    key: props.last_journal_activity,
                  },
                ];

                const averagePerDay = [
                  {
                    label: 'Chat Events',
                    value: props.average_all_chat_events_per_day,
                  },
                  {
                    label: 'Routine Events',
                    value: props.average_routine_events_per_day,
                  },
                  {
                    label: 'Routines Created',
                    value: props.average_routines_created_per_day,
                  },
                  {
                    label: 'Total Chats',
                    value: props.average_total_chats_per_day,
                  },
                  {
                    label: 'Task Interactions',
                    value: props.average_total_task_interaction_per_day,
                  },
                  {
                    label: 'Journal Opened',
                    value: props.average_journal_opened_per_day,
                  },
                ];

                return (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-2 block">
                      App interactions
                    </label>
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                          Total Events
                        </p>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                          {totalEvents.map(({ label, value }) => (
                            <div
                              key={label}
                              className="border rounded-md p-4 text-center"
                            >
                              <p className="text-2xl font-bold">
                                {value || '0'}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {label}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                          Last Activity
                        </p>
                        <div className="border rounded-md p-4">
                          <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-8">
                            {lastActivity.map(({ label, key }) => (
                              <div
                                key={label}
                                className="flex justify-between items-center"
                              >
                                <span className="text-sm text-muted-foreground">
                                  {label}
                                </span>
                                <span className="text-sm font-semibold">
                                  {formatDate(key)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">
                          Average Per Day
                        </p>
                        <div className="border rounded-md p-4">
                          <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-8">
                            {averagePerDay.map(({ label, value }) => (
                              <div
                                key={label}
                                className="flex justify-between items-center"
                              >
                                <span className="text-sm text-muted-foreground">
                                  {label}
                                </span>
                                <span className="text-base font-semibold">
                                  {value || '0'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                User Plan Status
              </label>
              <div className="flex flex-wrap gap-2">
                {PLAN_OPTIONS.map((plan) => (
                  <button
                    key={plan.value}
                    onClick={() => handlePlanChange(plan.value)}
                    disabled={!!user.team}
                    title={
                      user.team
                        ? 'Plan is set by team membership tier. Manage in the Team section below.'
                        : undefined
                    }
                    className={`px-4 py-2 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      selectedPlan === plan.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-muted'
                    }`}
                  >
                    {plan.label}
                  </button>
                ))}
              </div>
              {user.team && (
                <p className="text-xs text-muted-foreground mt-2">
                  Plan is managed by team membership ({user.team.name} · tier:{' '}
                  {user.team.membership_tier}). Change this user's tier from the
                  Team section below.
                </p>
              )}
            </div>

            {user.is_trialing && (
              <div className="bg-primary/10 border border-primary rounded-md px-4 py-3 flex items-center gap-3">
                <span className="inline-flex items-center rounded-full bg-primary/20 border border-primary px-3 py-1 text-sm font-semibold text-primary">
                  Trial Active
                </span>
                <span className="text-sm text-muted-foreground">
                  Ends {user.trial_end ? user.trial_end.split('T')[0] : 'N/A'}
                </span>
                <Button
                  onClick={() => setShowTrialModal(true)}
                  variant="ghost"
                  size="icon"
                  className="ml-auto h-8 w-8"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            )}

            {(user.plan === 'plus_monthly' || user.plan === 'plus_annual') &&
              !user.team && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground mb-2 block">
                    Subscription
                  </label>
                  <Button
                    onClick={handleCancelSubscription}
                    variant="destructive"
                    disabled={
                      cancelSubscription.isPending || user.cancel_at_period_end
                    }
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {cancelSubscription.isPending
                      ? 'Cancelling...'
                      : user.cancel_at_period_end
                        ? 'Already Scheduled for Cancellation'
                        : 'Cancel Subscription'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Cancels subscription at the end of the billing period. User
                    will receive an email notification.
                  </p>
                </div>
              )}

            {user.team ? (
              <UserEditTeamSection
                team={user.team}
                userId={user.id}
                userEmail={user.email}
              />
            ) : (
              <CreateTrialTeamSection user={user} />
            )}

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                User Actions
              </label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setShowTrialModal(true)}
                  variant="outline"
                  disabled={!!user.team}
                  title={
                    user.team
                      ? 'User is on a team. Use the Team section to manage trials team-wide.'
                      : undefined
                  }
                >
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  Provide Trial
                </Button>
                <Button
                  onClick={() => setAccountsDialog('extend')}
                  variant="outline"
                  disabled={
                    !user.is_trialing ||
                    !user.trial_end ||
                    !user.plan ||
                    !!user.team
                  }
                >
                  Extend trial
                </Button>
                <Button
                  onClick={() => setAccountsDialog('payment')}
                  variant="outline"
                  disabled={!!user.team}
                >
                  Send payment link
                </Button>
                <Button
                  onClick={() => setShowDiscountModal(true)}
                  variant="outline"
                  disabled={!!user.team || user.plan !== 'basic'}
                  title={
                    user.team
                      ? 'User is on a team. Use Apply Discount in the Team section.'
                      : user.plan !== 'basic'
                        ? 'Discount codes can only be generated for users on the Basic plan.'
                        : undefined
                  }
                >
                  <Gift className="h-4 w-4 mr-2" />
                  Generate Discount Code
                </Button>
                <Button
                  onClick={() => setShowCreditsModal(true)}
                  variant="outline"
                  disabled={!!user.team}
                  title={
                    user.team
                      ? 'User is on a team. Use Add Team Credit in the Team section.'
                      : undefined
                  }
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Add Stripe invoice credit
                </Button>
                <Button
                  onClick={handleResetDailyQuota}
                  variant="outline"
                  disabled={resetDailyQuota.isPending}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {resetDailyQuota.isPending
                    ? 'Resetting...'
                    : 'Reset daily quota'}
                </Button>
                {!user.referred_by && (
                  <Button
                    onClick={() => setShowReferralModal(true)}
                    variant="outline"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Apply Referral
                  </Button>
                )}
              </div>
              {user.team && (
                <p className="text-xs text-muted-foreground mt-2">
                  Some user-level actions are disabled because plan and billing
                  are managed by the team. Use the Team section below for
                  team-wide changes.
                </p>
              )}
              {couponCode && (
                <div className="mt-3 p-3 bg-muted rounded-md space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Coupon code for {couponCode.planLabel}:
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-mono font-semibold">
                      {couponCode.code}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(couponCode.code);
                        toast.success('Code copied to clipboard');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Send the user this code. They enter it at checkout in the
                    app when they upgrade to {couponCode.planLabel}. It works
                    only for {couponCode.planLabel}, only on their account, and
                    only once.
                  </p>
                </div>
              )}
            </div>

            {hasChanges && (
              <p className="text-sm text-orange-600">
                You have unsaved changes
              </p>
            )}

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  disabled={updateUser.isPending || !hasChanges}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {updateUser.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={onBack}>
                  Cancel
                </Button>
              </div>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteUser.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {deleteUser.isPending ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>

            {updateUser.isError && (
              <p className="text-sm text-destructive">
                Error:{' '}
                {updateUser.error instanceof Error
                  ? updateUser.error.message
                  : 'Failed to update user'}
              </p>
            )}
          </CardContent>
        </Card>

        <DiscountModal
          isOpen={showDiscountModal}
          onClose={() => setShowDiscountModal(false)}
          onSubmit={handleGenerateCoupon}
          isPending={generateCoupon.isPending}
        />
        <CreditsModal
          isOpen={showCreditsModal}
          onClose={() => setShowCreditsModal(false)}
          userId={user.id}
        />
        <AddUsageCreditsModal
          isOpen={showAddUsageCreditsModal}
          onClose={() => setShowAddUsageCreditsModal(false)}
          userId={user.id}
        />
        <RemoveUsageCreditsModal
          isOpen={showRemoveUsageCreditsModal}
          onClose={() => setShowRemoveUsageCreditsModal(false)}
          wallet={{ kind: 'user', userId: user.id }}
          purchasedBalance={creditWallet?.personal_wallet?.purchased_balance}
          rateCentsPer1000={creditWallet?.rate_cents_per_1000}
        />
        <GrantTrialModal
          isOpen={showTrialModal}
          onClose={() => setShowTrialModal(false)}
          onSubmit={handleGrantTrial}
          isPending={grantTrial.isPending}
        />
        {accountsDialog === 'extend' && user.trial_end && (
          <ExtendTrialDialog
            onClose={() => setAccountsDialog(null)}
            name={user.email}
            currentEndMs={Date.parse(user.trial_end)}
            onExtend={(trialEnd) =>
              grantTrial.mutateAsync({
                userId: user.id,
                plan: user.plan ?? '',
                trial_end: trialEnd,
              })
            }
          />
        )}
        {accountsDialog === 'payment' && (
          <PaymentLinkDialog
            onClose={() => setAccountsDialog(null)}
            ownerPath={`/users/${user.id}`}
            name={user.email}
            recipient={user.email}
          />
        )}
        <ApplyReferralModal
          isOpen={showReferralModal}
          onClose={() => setShowReferralModal(false)}
          userId={user.id}
        />
      </div>
    </>
  );
}
